/**
 * Pure week-overview helpers (Phase 6).
 *
 * Composes the Phase 0 date function (`dateForDay`) with the Phase 4 completion
 * model (`computeDayProgress` / `dayDoneKey`) and the phase-2-aware
 * `templateForWeek`. No I/O, no re-derivation of date math — the WeekView
 * component and TrainingShell consume these.
 */
import type { PlanData } from '@/features/ai-plan/lib/types'
import type { CheckedMap, DayPosition } from '@/shared/bridge/trainingBridge'
import { dateForDay, type AnchorInput } from '@/shared/utils/dateMath'
import { computeDayProgress, dayDoneKey, firstIncompletePosition, templateForWeek } from '@/features/training/lib/dayProgress'

export type WeekDayStatus = 'complete' | 'in-progress' | 'not-started'

export interface WeekDayRow {
  dayOfWeek: number
  label: string
  focus: string
  date: string // YYYY-MM-DD (local)
  isRest: boolean
  status: WeekDayStatus
}

export interface WeekProgress {
  doneDays: number
  totalDays: number
  pct: number
}

function anchorInput(plan: PlanData): AnchorInput {
  return {
    totalWeeks: Math.max(1, plan.total_weeks || 1),
    officialTestDate: plan.official_test_date ?? '',
    generatedAt: plan.generated_at ?? '',
    planStartDate: (plan as any).plan_start_date ?? undefined,
  }
}

function isDayComplete(plan: PlanData, pos: DayPosition, checked: CheckedMap): boolean {
  if (checked[dayDoneKey(pos)]) return true
  const day = templateForWeek(plan, pos.week).find(d => d.day_of_week === pos.dayOfWeek) ?? null
  return computeDayProgress(day, pos, checked).pct >= 100
}

export function dayStatus(plan: PlanData, pos: DayPosition, checked: CheckedMap): WeekDayStatus {
  if (isDayComplete(plan, pos, checked)) return 'complete'
  const day = templateForWeek(plan, pos.week).find(d => d.day_of_week === pos.dayOfWeek) ?? null
  return computeDayProgress(day, pos, checked).done > 0 ? 'in-progress' : 'not-started'
}

export function getWeekRows(plan: PlanData, week: number, checked: CheckedMap, today: string): WeekDayRow[] {
  const input = anchorInput(plan)
  let rows = templateForWeek(plan, week).map(day => {
    const pos: DayPosition = { week, dayOfWeek: day.day_of_week }
    return {
      dayOfWeek: day.day_of_week,
      label: day.label || `Day ${day.day_of_week}`,
      focus: day.focus ?? '',
      date: dateForDay(input, week, day.day_of_week, today),
      isRest: day.is_rest === true,
      status: dayStatus(plan, pos, checked),
    }
  })
  // For plans with explicit start (new mid-week starts), week 1 is the partial calendar week from the start date
  if (week === 1 && (plan as any).plan_start_date) {
    rows = rows.filter(r => r.date >= (plan as any).plan_start_date)
  }
  return rows
}

export function weekProgress(plan: PlanData, week: number, checked: CheckedMap): WeekProgress {
  const training = templateForWeek(plan, week).filter(d => !d.is_rest)
  const totalDays = training.length
  let doneDays = 0
  for (const day of training) {
    if (isDayComplete(plan, { week, dayOfWeek: day.day_of_week }, checked)) doneDays++
  }
  const pct = totalDays > 0 ? Math.round((doneDays / totalDays) * 100) : 0
  return { doneDays, totalDays, pct }
}

/**
 * The week containing `today` (date-based). Falls back to the first incomplete
 * week when today is outside the plan's date range.
 */
export function currentWeek(plan: PlanData, today: string, checked: CheckedMap): number {
  const input = anchorInput(plan)
  const start = new Date(`${dateForDay(input, 1, 1, today)}T00:00:00`).getTime()
  const now = new Date(`${today}T00:00:00`).getTime()
  if (Number.isFinite(start) && Number.isFinite(now)) {
    const week = Math.floor((now - start) / 86400000 / 7) + 1
    if (week >= 1 && week <= input.totalWeeks) return week
  }
  return firstIncompletePosition(plan, checked).week
}

export function positionForDate(plan: PlanData, today: string, checked: CheckedMap): DayPosition {
  const total = Math.max(1, plan.total_weeks || 1)
  for (let week = 1; week <= total; week++) {
    const row = getWeekRows(plan, week, checked, today).find(day => day.date === today)
    if (row) return { week, dayOfWeek: row.dayOfWeek }
  }
  return firstIncompletePosition(plan, checked)
}
